import React, { FC, useState } from 'react';
import { Button, Col, Descriptions, Input, Row, Tag, Typography } from 'antd';
import FileSaver from 'file-saver';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';

import { GetClanWarDefenseTeamResponseDto } from '@services/_api';
import { getClanWarDefenseTeam } from '@services/cstool/recovery-account';

import ClientVersionSearchSelect from './client-version-search-select';
import { useGetCurrentProgressContext } from './context';

const { Title } = Typography;

type Status = 'success' | 'no-result' | 'error';

const ClanWarDefenseTeamSearch: FC = () => {
  const { environment } = useGetCurrentProgressContext();
  const [clanId, setClanId] = useState<string>();
  const [userId, setUserId] = useState<string>();
  const [seasonId, setSeasonId] = useState<string>();
  const [tournamentId, setTournamentId] = useState<string>();
  const [warId, setWarId] = useState<string>();
  const [clientVersion, setClientVersion] = useState<string>();
  const [data, setData] = useState<GetClanWarDefenseTeamResponseDto>();
  const [status, setStatus] = useState<Status>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isFetching, setIsFetching] = useState(false);

  const canSearch = !!userId && !!tournamentId && !!warId && !!environment && !!clientVersion;

  const handleSearch = async () => {
    if (!userId || !tournamentId || !warId || !environment || !clientVersion) return;
    setIsFetching(true);
    setData(undefined);
    setStatus(undefined);
    setErrorMessage(undefined);
    const response = await getClanWarDefenseTeam({
      userId,
      tournamentId,
      warId,
      clanId,
      seasonId,
      environment,
      clientVersion,
    }).finally(() => {
      setIsFetching(false);
    });
    if (response.ok) {
      if (response.data) {
        setData(response.data);
        setStatus('success');
      } else {
        setStatus('no-result');
      }
    } else {
      setStatus('error');
      setErrorMessage(response.error?.message);
    }
  };

  const handleDownload = () => {
    if (!data?.lineup) return;
    const blob = new Blob([JSON.stringify(data.lineup, null, 2)], {
      type: 'text/plain;charset=utf-8',
    });
    FileSaver.saveAs(
      blob,
      `clan-war-defense-${data.userId}-${data.tournamentId}-${data.warId}.txt`,
    );
  };

  return (
    <div className="flex flex-col">
      <Row gutter={[16, 16]} className="mb-4">
        <Col>
          <Title level={5} className="mr-2">
            Clan ID
          </Title>
          <Input
            value={clanId}
            placeholder="Optional"
            style={{ width: 180 }}
            onChange={(e) => setClanId(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>
        <Col>
          <Title level={5} className="mr-2">
            User ID
          </Title>
          <Input
            value={userId}
            placeholder="Enter user ID"
            style={{ width: 250 }}
            onChange={(e) => setUserId(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>
        <Col>
          <Title level={5} className="mr-2">
            Season ID
          </Title>
          <Input
            value={seasonId}
            placeholder="Optional"
            style={{ width: 120 }}
            onChange={(e) => setSeasonId(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>
        <Col>
          <Title level={5} className="mr-2">
            Tournament ID
          </Title>
          <Input
            value={tournamentId}
            placeholder="Enter tournament ID"
            style={{ width: 160 }}
            onChange={(e) => setTournamentId(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>
        <Col>
          <Title level={5} className="mr-2">
            War ID
          </Title>
          <Input
            value={warId}
            placeholder="Enter war ID"
            style={{ width: 120 }}
            onChange={(e) => setWarId(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>
        <Col>
          <Title level={5} className="mr-2">
            Client Version
          </Title>
          <ClientVersionSearchSelect
            environment={environment}
            value={clientVersion}
            style={{ width: 200 }}
            onChange={setClientVersion}
            disabled={!environment}
          />
        </Col>
        <Col className="flex items-end">
          <Button
            htmlType="submit"
            type="primary"
            loading={isFetching}
            onClick={handleSearch}
            disabled={!canSearch}
          >
            <SearchOutlined />
          </Button>
        </Col>
      </Row>
      <Row gutter={[16, 16]} align="middle" className="mb-4">
        <Col>
          {status === 'success' && <Tag color="green">Success</Tag>}
          {status === 'no-result' && <Tag color="orange">No Result</Tag>}
          {status === 'error' && <Tag color="red">{`Error : ${errorMessage}`}</Tag>}
        </Col>
        {status === 'success' && data?.lineup && (
          <Col>
            <Button icon={<DownloadOutlined />} onClick={handleDownload}>
              Download Lineup
            </Button>
          </Col>
        )}
        {status === 'success' && !data?.lineup && (
          <Col>
            <Tag color="orange">No lineup data</Tag>
          </Col>
        )}
      </Row>
      {status === 'success' && data && (
        <Descriptions size="small" column={5} className="mb-4">
          <Descriptions.Item label="Clan ID">{data.clanId ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Season ID">{data.seasonId ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Tournament ID">{data.tournamentId}</Descriptions.Item>
          <Descriptions.Item label="War ID">{data.warId}</Descriptions.Item>
          <Descriptions.Item label="Defense Team ID">{data.defenseTeamId}</Descriptions.Item>
        </Descriptions>
      )}
    </div>
  );
};

export default ClanWarDefenseTeamSearch;
